import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import Invoices from "@/pages/Invoices";
import TaxReturns from "@/pages/TaxReturns";
import Tasks from "@/pages/Tasks";
import Team from "@/pages/Team";
import AIAssistant from "@/pages/AIAssistant";
import Settings from "@/pages/Settings";
import Portals from "@/pages/Portals";
import Banks from "@/pages/Banks";
import Expenses from "@/pages/Expenses";
import Cash from "@/pages/Cash";
import FirmSettings from "@/pages/FirmSettings";
import HSNFinder from "@/pages/HSNFinder";
import WhatsAppConnect from "@/pages/WhatsAppConnect";
import Billing from "@/pages/Billing";
import PendingApproval from "@/pages/PendingApproval";

// Public marketing pages
import Landing from "@/pages/marketing/Landing";
import Pricing from "@/pages/marketing/Pricing";
import About from "@/pages/marketing/About";
import Contact from "@/pages/marketing/Contact";
import Terms from "@/pages/marketing/Terms";
import Privacy from "@/pages/marketing/Privacy";
import Refund from "@/pages/marketing/Refund";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500 font-mono text-sm">[ loading... ]</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public marketing site (always accessible — works for Razorpay reviewers) */}
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund" element={<Refund />} />

            {/* Auth */}
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/pending-approval" element={<Protected><SubscriptionGate><PendingApproval /></SubscriptionGate></Protected>} />

            {/* Protected app — lives at /dashboard, /clients, /invoices etc. */}
            <Route path="/dashboard" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<Dashboard />} />
            </Route>
            <Route path="/clients" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<Clients />} />
            </Route>
            <Route path="/invoices" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<Invoices />} />
            </Route>
            <Route path="/banks" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<Banks />} />
            </Route>
            <Route path="/expenses" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<Expenses />} />
            </Route>
            <Route path="/cash" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<Cash />} />
            </Route>
            <Route path="/returns" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<TaxReturns />} />
            </Route>
            <Route path="/tasks" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<Tasks />} />
            </Route>
            <Route path="/team" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<Team />} />
            </Route>
            <Route path="/ai" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<AIAssistant />} />
            </Route>
            <Route path="/portals" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<Portals />} />
            </Route>
            <Route path="/hsn" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<HSNFinder />} />
            </Route>
            <Route path="/whatsapp" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<WhatsAppConnect />} />
            </Route>
            <Route path="/firm" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<FirmSettings />} />
            </Route>
            <Route path="/settings" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<Settings />} />
            </Route>
            <Route path="/app/billing" element={<Protected><SubscriptionGate><Layout /></SubscriptionGate></Protected>}>
              <Route index element={<BillingWrapped />} />
            </Route>

            {/* Catch-all → marketing landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

function BillingWrapped() {
  const { user } = useAuth();
  const forced = !!(user?.subscription_status && !user.subscription_status.is_active && !user.subscription_status.founding);
  return <Billing forced={forced} />;
}

/**
 * Subscription gate — enforces three states:
 *  1. Paid but not yet approved → /pending-approval
 *  2. Trial expired / no active sub → /app/billing
 *  3. Active (trial or paid+approved) → pass through
 */
function SubscriptionGate({ children }) {
  const { user } = useAuth();
  const sub = user?.subscription_status;
  const path = window.location.pathname;
  if (!sub) return children;

  if (sub.needs_admin_approval) {
    if (path.startsWith("/pending-approval")) return children;
    return <Navigate to="/pending-approval" replace />;
  }
  if (sub.is_active) return children;
  if (path.startsWith("/app/billing") || path.startsWith("/settings")) return children;
  return <Navigate to="/app/billing" replace />;
}
